import { useCallback, useEffect, useState } from "react";
import { ChevronRight, Database, Pencil, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, LoadingRows } from "@/components/shared";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { cn } from "@/lib/utils";

const valueToText = (v) => (typeof v === "string" ? v : v?.$base64 ? `[binário base64] ${v.$base64}` : JSON.stringify(v));

const EMPTY_COND = { field: "", text: "" };

export default function KvExplorer({ db, onChange }) {
  const [mode, setMode] = useState("prefix"); // prefix | where
  const [prefix, setPrefix] = useState("");
  const [query, setQuery] = useState("");
  const [conds, setConds] = useState([EMPTY_COND]);
  const [where, setWhere] = useState([]);
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState(null);
  const [pages, setPages] = useState([""]);
  const [next, setNext] = useState(null);
  const [editing, setEditing] = useState(null); // {key, value, isNew}
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (after = "") => {
    setItems(null);
    try {
      const params = new URLSearchParams({ after, limit: 50 });
      if (mode === "where" && where.length) where.forEach((w) => params.append("where", w));
      else params.set("prefix", prefix);
      const { data } = await api.get(`/db/${db}/kv?${params.toString()}`);
      setItems(data.items);
      setNext(data.next);
      setMeta(data.scanned != null ? { scanned: data.scanned, took_us: data.took_us } : null);
    } catch (e) {
      toast.error(errMsg(e));
      setItems([]);
    }
  }, [db, prefix, mode, where]);

  const submitWhere = (e) => {
    e.preventDefault();
    const w = conds.filter((c) => c.field.trim() && c.text.trim()).map((c) => `${c.field.trim()}:${c.text.trim()}`);
    if (!w.length) return toast.error("Informe campo e texto");
    setWhere(w);
  };
  const setCond = (i, patch) => setConds((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  useEffect(() => { setPages([""]); load(""); }, [load]);

  const goNext = () => { setPages((p) => [...p, next]); load(next); };
  const goPrev = () => { const p = pages.slice(0, -1); setPages(p); load(p[p.length - 1]); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let value = editing.value;
      try { value = JSON.parse(editing.value); } catch { /* texto puro */ }
      await api.put(`/db/${db}/kv/${encodeURIComponent(editing.key)}`, { value }, { headers: { "Content-Type": "application/json" } });
      toast.success(`Chave "${editing.key}" salva`);
      setEditing(null);
      load(pages[pages.length - 1]);
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/db/${db}/kv/${encodeURIComponent(toDelete)}`);
      toast.success(`Chave "${toDelete}" apagada`);
      setToDelete(null);
      load(pages[pages.length - 1]);
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="kv-explorer">
      <div className="flex gap-1 mb-3 text-xs" data-testid="kv-mode-switch">
        {[["prefix", "Prefixo da chave"], ["where", "Buscar por campo"]].map(([m, l]) => (
          <button key={m} type="button" onClick={() => { setMode(m); setPages([""]); }} data-testid={`kv-mode-${m}`}
            className={cn("px-3 py-1.5 rounded-sm border transition-colors", mode === m ? "border-orange-600 text-white bg-zinc-900" : "border-zinc-800 text-zinc-400 hover:text-white")}>{l}</button>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        {mode === "prefix" ? (
        <form onSubmit={(e) => { e.preventDefault(); setPrefix(query); }} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filtrar por prefixo (ex.: user:)" data-testid="kv-prefix-input" className="pl-9 bg-transparent border-zinc-800 rounded-sm font-mono" />
          </div>
          <Button type="submit" variant="outline" data-testid="kv-prefix-submit" className="rounded-sm border-zinc-700 btn-tactile">Filtrar</Button>
        </form>
        ) : (
        <form onSubmit={submitWhere} className="flex-1 flex flex-col gap-2" data-testid="kv-where-form">
          {conds.map((c, i) => (
            <div key={i} className="flex gap-2">
              <Input value={c.field} onChange={(e) => setCond(i, { field: e.target.value })} placeholder="campo (ex.: pai)" data-testid={`kv-where-field-${i}`} className="w-40 bg-transparent border-zinc-800 rounded-sm font-mono" />
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input value={c.text} onChange={(e) => setCond(i, { text: e.target.value })} placeholder='texto (ex.: severino) — use "aspas" para palavra exata' data-testid={`kv-where-text-${i}`} className="pl-9 bg-transparent border-zinc-800 rounded-sm font-mono" />
              </div>
              {conds.length > 1 && <Button type="button" variant="ghost" onClick={() => setConds((cs) => cs.filter((_, j) => j !== i))} data-testid={`kv-where-remove-${i}`} className="rounded-sm px-2"><X className="h-4 w-4" /></Button>}
              {i === conds.length - 1 && (
                <>
                  {conds.length < 4 && <Button type="button" variant="ghost" onClick={() => setConds((cs) => [...cs, EMPTY_COND])} data-testid="kv-where-add" className="rounded-sm px-2 text-zinc-400" title="Adicionar condição (E)"><Plus className="h-4 w-4" /></Button>}
                  <Button type="submit" variant="outline" data-testid="kv-where-submit" className="rounded-sm border-zinc-700 btn-tactile">Buscar</Button>
                </>
              )}
            </div>
          ))}
        </form>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => load(pages[pages.length - 1])} data-testid="kv-refresh-button" className="rounded-sm border-zinc-700 btn-tactile"><RefreshCw className="h-4 w-4" /></Button>
          <Button onClick={() => setEditing({ key: "", value: "", isNew: true })} data-testid="kv-add-button" className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile"><Plus className="h-4 w-4 mr-1" /> Inserir</Button>
        </div>
      </div>

      {!items ? (
        <LoadingRows n={6} />
      ) : items.length === 0 ? (
        <EmptyState icon={Database} title={mode === "where" && where.length ? `Nenhum registro com ${where.join(" e ")}` : prefix ? `Nada com o prefixo "${prefix}"` : "Nenhuma chave"} description={mode === "where" ? "A busca casa por prefixo de palavra em cada campo. Se os dados foram importados antes do índice existir, use a aba Índice → Reindexar." : "Insira pela interface ou via API com PUT /api/db/{banco}/kv/{chave}."} testId="kv-empty"
          action={<Button onClick={() => setEditing({ key: prefix, value: "", isNew: true })} variant="outline" data-testid="kv-empty-add" className="rounded-sm border-zinc-700">Inserir chave</Button>} />
      ) : (
        <div className="panel overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="overline-label">Chave</TableHead>
                <TableHead className="overline-label">Valor</TableHead>
                <TableHead className="overline-label text-right w-24">Tamanho</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it, i) => (
                <TableRow key={it.key} className="border-zinc-800 hover:bg-zinc-800/60 row-enter" style={{ animationDelay: `${i * 20}ms` }} data-testid={`kv-row-${it.key}`}>
                  <TableCell className="font-mono text-sm text-orange-400 max-w-[220px] truncate">{it.key}</TableCell>
                  <TableCell className="font-mono text-xs text-zinc-300 max-w-[420px] truncate">{valueToText(it.value)}</TableCell>
                  <TableCell className="text-right text-xs text-zinc-500 font-mono">{fmtBytes(it.size)}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => setEditing({ key: it.key, value: valueToText(it.value), isNew: false })} data-testid={`kv-edit-${it.key}`} className="p-1.5 text-zinc-400 hover:text-white rounded-sm" title="Editar"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setToDelete(it.key)} data-testid={`kv-delete-${it.key}`} className="p-1.5 text-zinc-400 hover:text-red-400 rounded-sm" title="Apagar"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800 text-xs text-zinc-500">
            <span data-testid="kv-page-info">Página {pages.length} · {items.length} itens{meta && <span className="text-zinc-600"> · {meta.scanned} entradas varridas · {meta.took_us >= 1000 ? `${(meta.took_us / 1000).toFixed(1)} ms` : `${meta.took_us} µs`}</span>}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" disabled={pages.length <= 1} onClick={goPrev} data-testid="kv-prev-page" className="rounded-sm">Anterior</Button>
              <Button size="sm" variant="ghost" disabled={!next} onClick={goNext} data-testid="kv-next-page" className="rounded-sm">Próxima <ChevronRight className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </div>
      )}

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="bg-zinc-900 border-zinc-800 w-full sm:max-w-lg overflow-y-auto" data-testid="kv-edit-sheet">
          {editing && (
            <form onSubmit={save} className="flex flex-col min-h-full">
              <SheetHeader>
                <SheetTitle className="font-display">{editing.isNew ? "Inserir chave" : "Editar chave"}</SheetTitle>
                <SheetDescription>Valores em JSON válido são armazenados como JSON; qualquer outro texto é gravado como está.</SheetDescription>
              </SheetHeader>
              <div className="flex-1 space-y-4 py-6">
                <div className="space-y-2">
                  <Label>Chave</Label>
                  <Input value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value })} readOnly={!editing.isNew} required data-testid="kv-key-input" className="bg-transparent border-zinc-800 rounded-sm font-mono" />
                </div>
                <div className="space-y-2">
                  <Label>Valor</Label>
                  <Textarea rows={8} value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} data-testid="kv-value-input" className="bg-transparent border-zinc-800 rounded-sm font-mono text-xs" placeholder='{"nome": "Ana"} ou texto livre' />
                </div>
              </div>
              <SheetFooter className="sticky bottom-0 bg-zinc-900 py-3 border-t border-zinc-800">
                <Button type="button" variant="ghost" onClick={() => setEditing(null)} data-testid="kv-edit-cancel" className="rounded-sm">Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="kv-edit-save" className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile">Salvar</Button>
              </SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title={`Apagar "${toDelete}"?`} description="A chave será removida do banco (tombstone no WAL, compactada depois)." onConfirm={remove} testId="confirm-delete-key" />
    </div>
  );
}
